import clsx from "clsx";
import { useState } from "react";

import RenderIf from "common/components/RenderIf";
import SenderProfile from "common/components/Templates/TemplatePreview/SenderProfile";
import Switch from "common/components/ui/Switch";
import { TEMPLATE_PREVIEW_ACCOUNTS } from "common/constants";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type { LinkedInProfile, Template } from "common/types";

import useTemplatePreview from "../../../datahooks/useTemplatePreview";
import PreviewContent from "./PreviewContent";
import PreviewHeader from "./PreviewHeader";

interface TemplatePreviewProps {
  updatedTemplate: Template;
  previewTemplate: Template | undefined;
  onUpdatePreview: () => void;
  senderProfiles: LinkedInProfile[];
  targetProfiles?: LinkedInProfile[];
  campaignData?: {
    campaignId: string;
    isTranslateEnabled: boolean;
    sourceLanguageCode: string;
  };
}

export default function TemplatePreview({
  updatedTemplate,
  previewTemplate,
  onUpdatePreview,
  targetProfiles,
  senderProfiles,
  campaignData = undefined,
}: TemplatePreviewProps) {
  const isDesktop = useTwBreakpoint("lg");
  const [senderProfile, setSenderProfile] = useState(senderProfiles[0]);
  const [targetProfileIndex, setTargetProfileIndex] = useState(0);
  const [shouldShowTranslatedText, setShouldShowTranslatedText] =
    useState(true);

  const finalTargetProfiles = targetProfiles?.length
    ? targetProfiles
    : TEMPLATE_PREVIEW_ACCOUNTS;

  const {
    injectContent,
    translateContent,
    refetchPreview,
    previewError,
    isLoadingPreview,
  } = useTemplatePreview({
    previewData: {
      template: previewTemplate,
      targetProfileId: finalTargetProfiles[targetProfileIndex].id,
      senderProfileId: senderProfile ? senderProfile.id : undefined,
      isTranslateEnabled: campaignData
        ? campaignData.isTranslateEnabled
        : false,
      sourceLanguageCode: campaignData?.sourceLanguageCode,
      campaignId: campaignData?.campaignId,
    },
  });

  function onRefetch() {
    if (previewError) {
      refetchPreview();
    } else {
      onUpdatePreview();
    }
  }

  const isTemplateUnchanged =
    previewTemplate &&
    updatedTemplate &&
    previewTemplate.message.trim() === updatedTemplate.message.trim() &&
    ("subject" in previewTemplate &&
    "subject" in updatedTemplate &&
    !!previewTemplate.subject &&
    !!updatedTemplate.subject
      ? previewTemplate.subject === updatedTemplate.subject
      : true) &&
    Object.keys(previewTemplate.ai_descriptors).every(
      (key) =>
        previewTemplate.ai_descriptors[key] ===
        updatedTemplate.ai_descriptors[key],
    );

  const isEmptyTemplate =
    !updatedTemplate.message ||
    (updatedTemplate.type === "INMAIL_TEMPLATE" && !updatedTemplate.subject) ||
    Object.values(updatedTemplate.ai_descriptors).some(
      (variableValue) => !variableValue,
    );

  const content = {
    displayMessage: "",
    displaySubject: "",
  };

  if (
    campaignData?.isTranslateEnabled &&
    translateContent &&
    shouldShowTranslatedText
  ) {
    content.displayMessage = translateContent.message;
    content.displaySubject = translateContent.subject;
  } else if (injectContent) {
    content.displayMessage = injectContent.message;
    content.displaySubject = injectContent.subject;
  }

  const toggleTranslatedTextComponent = (
    <RenderIf
      condition={campaignData?.isTranslateEnabled && !!previewTemplate?.message}
    >
      <div className="flex items-center gap-1 px-1">
        <span className="text-xs-bold">Show translated</span>
        <Switch
          size="sm"
          checked={shouldShowTranslatedText}
          onCheckedChange={setShouldShowTranslatedText}
        />
      </div>
    </RenderIf>
  );

  return (
    <section className="flex min-h-80 flex-1 flex-col overflow-hidden rounded-3xl border border-black-200 lg:basis-1/2">
      <PreviewHeader
        targetProfiles={finalTargetProfiles}
        currentTargetIndex={targetProfileIndex}
        setCurrentTargetIndex={setTargetProfileIndex}
      />
      {!isDesktop && toggleTranslatedTextComponent}
      <div className="relative flex flex-1 flex-col overflow-hidden">
        <div
          className={clsx(
            "flex w-full items-center gap-2 pl-2 pr-3 pt-3 justify-between",
            senderProfiles.length > 1 && "mb-3",
          )}
        >
          <SenderProfile
            profileOptions={senderProfiles}
            senderProfile={senderProfile}
            setSenderProfile={setSenderProfile}
          />
          {isDesktop && toggleTranslatedTextComponent}
        </div>

        <PreviewContent
          message={content.displayMessage}
          subject={content.displaySubject}
          isLoading={isLoadingPreview}
          error={previewError}
          onRefetch={
            (!isTemplateUnchanged || previewError) &&
            !isEmptyTemplate &&
            onRefetch
          }
        />
      </div>
    </section>
  );
}
